import {
    Autocomplete,
    AutocompleteProps,
    CircularProgress,
    FilledInputProps,
    InputProps as MuiInputProps,
    OutlinedInputProps,
    TextField,
    TextFieldProps,
} from '@mui/material';
import React from 'react';
import { NEVER, Observable, Subject } from 'rxjs';
import { debounceTime, switchMap, tap } from 'rxjs/operators';

import { GeneralConstants } from 'app/constants/general-constants';
import { KnpPaginationData } from 'app/model/knp-pagination-data';

import { TimingConstants } from '../constants/timing-constants';

type ChangeEvent = React.ChangeEvent<{ name?: string; value: unknown }>;

interface DelayedSearchState<T> {
    open: boolean;
    loading: boolean;
    options: T[];
}

type OmitProps =
    | 'autoComplete'
    | 'filterOptions'
    | 'filterSelectedOptions'
    | 'loading'
    | 'onChange'
    // | 'onClose'
    | 'onInputChange'
    // | 'onOpen'
    | 'open'
    | 'options'
    | 'ref'
    | 'renderInput';

type FreeSoloMapping<FreeSolo> = FreeSolo extends true ? string : never;

type ValueMapping<T, FreeSolo> = FreeSoloMapping<FreeSolo> | T;

export interface DelayedSearchProps<T, FreeSolo extends boolean | undefined, S = KnpPaginationData<T>>
    extends Omit<AutocompleteProps<T, false, undefined, FreeSolo>, OmitProps>,
        Pick<TextFieldProps, 'error' | 'helperText' | 'required'> {
    value: ValueMapping<T, FreeSolo> | null;
    label?: React.ReactNode;
    name?: string;
    className?: string;
    variant?: 'filled' | 'outlined' | 'standard';

    onChange: (event: ChangeEvent, value: ValueMapping<T, FreeSolo> | undefined) => void;

    onSearch: (data: string) => Observable<S>;

    getOptionLabel: (data?: ValueMapping<T, FreeSolo> | undefined) => string;

    isOptionEqualToValue?: (option: T, value: ValueMapping<T, FreeSolo>) => boolean;

    getOptionDisabled?: (option: T) => boolean;

    mapDataToOptions: (data: S) => T[];
    // eslint-disable-next-line @typescript-eslint/naming-convention
    InputProps?: Partial<FilledInputProps> | Partial<MuiInputProps> | Partial<OutlinedInputProps>;
}

export class DelayedSearch<T, S, F extends boolean | undefined> extends React.Component<
    DelayedSearchProps<T, F, S>,
    DelayedSearchState<T>
> {
    private delayedSubject = new Subject<string>();
    public constructor(props: DelayedSearchProps<T, F, S>) {
        super(props);

        this.state = {
            open: false,
            loading: false,
            options: [],
        };

        this.delayedSubject
            .pipe(
                tap(() => this.setState({ loading: true })),
                debounceTime(TimingConstants.TYPING_DELAY),
                switchMap((data) => {
                    // invalid detection
                    const label =
                        'string' === typeof this.props.value
                            ? this.props.value
                            : this.props.getOptionLabel(this.props.value || undefined);
                    if (label === data) {
                        this.setState({ loading: false });

                        return NEVER;
                    }

                    return this.props.onSearch(data);
                }),
                tap(() => this.setState({ loading: false })),
            )
            .subscribe((data) => {
                this.setState({
                    options: this.props.mapDataToOptions(data),
                });
            });
    }

    public render(): React.ReactNode {
        const { error, required, helperText, onChange, name, label, variant, InputProps, placeholder, ...other } =
            this.props;

        return (
            <Autocomplete
                {...other}
                options={this.state.options}
                onChange={(evt, value) => {
                    onChange(
                        {
                            ...evt,
                            currentTarget: {
                                ...evt.currentTarget,
                                name,
                                value,
                            },
                            target: {
                                ...evt.target,
                                name,
                                value,
                            },
                        },
                        value === GeneralConstants.COMPONENT_VALUE_NULL ? undefined : value,
                    );
                }}
                autoComplete
                onInputChange={(event, newInputValue) => {
                    this.delayedSubject.next(newInputValue);
                }}
                /* The server returns filtered results so we don't want to filter here */
                filterSelectedOptions
                filterOptions={(x) => x}
                clearOnBlur
                loading={this.state.loading}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        placeholder={placeholder}
                        label={label}
                        required={required}
                        name={name}
                        error={error}
                        helperText={helperText}
                        variant={variant || 'outlined'}
                        InputProps={{
                            ...params.InputProps,
                            onBlur: (evt) => {
                                if (
                                    other.freeSolo &&
                                    evt.target.value !== other.getOptionLabel(other.value || undefined)
                                ) {
                                    onChange(evt, (evt.target.value as ValueMapping<T, F>) || undefined);
                                }
                            },
                            endAdornment: (
                                <React.Fragment>
                                    {this.state.loading ? <CircularProgress color='inherit' size={20} /> : undefined}
                                    {params.InputProps.endAdornment}
                                </React.Fragment>
                            ),
                            ...InputProps,
                        }}
                    />
                )}
            />
        );
    }
}
